import { Component } from 'react';

import _ from 'lodash';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { voiceOptions } from '../../assets/sounds/widgetAudio';
import { styled } from '../../assets/theme';
import { ReactGrid } from '../ReactGrid';
import WatchIndex from '../WatchIndex';
import { WatchRotation } from '../WatchRotation';
import defaultWatchGrid from './defaultWatchGrid.json';

import { AddWatch, IAddWatchOptions } from './AddWatch';

const GRID_SIZE = 20;
const WIDGET_WIDTH = 5;

interface IWatchBoxGridItem {
	type: string,
	wIndex: string,
	time: string,
	unit: string,
	key: number,
	x: number,
	width: number,
	y: number,
	h: number,
	isActive: boolean
}

interface ILayoutItem {
	x: number,
	y: number,
	w: number,
	h: number,
	i: string,
	static: boolean,
}

interface IWatchesState {
	watchBoxGrid: IWatchBoxGridItem[],
	layouts: ILayoutItem[],
	showModal: boolean
}

export class Watches extends Component<{}, IWatchesState> {
	constructor(props: {}) {
		super(props);
	const grid = localStorage.getItem('watchbox_grid');
		const localGrid = grid ? JSON.parse(grid) : undefined;
		let watchBoxGrid: IWatchBoxGridItem[];
		if (localGrid) {
			if (Date.parse(localGrid.timestamp) > 1516589595947) {
				// removing ESC starting 08/16/2019
				watchBoxGrid = (localGrid.watchBoxGrid as IWatchBoxGridItem[]).filter(item => item.wIndex !== 'ESC');
			} else {
				watchBoxGrid = defaultWatchGrid.watchBoxGrid;
			}
		} else {
			watchBoxGrid = defaultWatchGrid.watchBoxGrid;
		}

		this.state = {
			layouts: [],
			showModal: false,
			watchBoxGrid,
		};
	}

	public getWidgets = () => this.state.watchBoxGrid.map((widgetConfig) => {
		let widget: JSX.Element;
		const title = '';
		switch (widgetConfig.type) {
		case 'watchIndex':
			widget = (<WatchIndex
				id={widgetConfig.key}
				title={`${widgetConfig.wIndex} - ${widgetConfig.time}${widgetConfig.unit}`}
				voices={voiceOptions[`${widgetConfig.wIndex}${widgetConfig.time}${widgetConfig.unit === '1Day' ? '1D' : ''}`]}
				symbol={widgetConfig.wIndex}
				time={widgetConfig.time}
				unit={widgetConfig.unit}
				handleRemoveWidget={this.handleRemoveWidget} />
			);
			break;
		case 'watchRotation':
			widget = (<WatchRotation
				id={widgetConfig.key}
				title="Rotation"
				handleRemoveWidget={this.handleRemoveWidget} />
			);
			break;
		default:
			widget = <div>Error: Unknown widget type found of {widgetConfig.type} type</div>
			break;
		}
		return (
			<PanelWrapper
				key={widgetConfig.key}
				title={title}
				data-grid={{
					h: widgetConfig.h,
					maxH: widgetConfig.h * 4,
					w: widgetConfig.width,
					x: widgetConfig.x,
					y: widgetConfig.y,
				}}>
				{widget}
			</PanelWrapper>
		);
	});

	public handleOpenModal = () => {
		this.setState({ showModal: true });
	}

	public handleCloseModal = () => {
		this.setState({ showModal: false });
	}

	public handleAddWidget = (options: IAddWatchOptions) => {
		const { watchBoxGrid } = this.state;
		const widget = {
      h: options.h,
      isActive: false,
      key: Math.random() * 10 ** 18,
      time: options.time,
      type: options.type,
      unit: options.unit,
      wIndex: options.wIndexLabel,
      width: options.width,
      x: watchBoxGrid.length * WIDGET_WIDTH % GRID_SIZE,
      y: 0
    };

		let newGrid = _.cloneDeep(watchBoxGrid);
		newGrid = newGrid.concat(widget);
		this.saveGridState(newGrid);
	}

	public handleRemoveWidget = (key: number) => {
		const grid = _.cloneDeep(this.state.watchBoxGrid);
		const newGrid = _.reject(grid, (widget: IWatchBoxGridItem) => widget.key === key);
		this.saveGridState(newGrid);
	}

	public layoutChange = (layouts: ILayoutItem[]) => {
		const { watchBoxGrid } = this.state;
		const grid = _.cloneDeep(watchBoxGrid);
		if (grid) {
			const newGrid = _.cloneDeep(grid);
			layouts.forEach(layout => {
				const currentGrid = newGrid.find(gridElement => gridElement.key === Number(layout.i));
				if (currentGrid) {
					currentGrid.width = layout.w;
					currentGrid.x = layout.x;
					currentGrid.y = layout.y;
					currentGrid.h = layout.h;
				}
			});
			this.saveGridState(newGrid);
		}
	}

	public activateWidget = (key: number) => {
		const grid = _.cloneDeep(this.state.watchBoxGrid);
		const currentWatch = _.find(grid, widget => widget.key === key);
		if (currentWatch && !currentWatch.isActive) {
			const currentGrid = _.map(grid, watch => {
				if (watch.key === key) {
					watch.isActive = true;
				} else if (watch.isActive) {
					watch.isActive = false;
				}
				return watch;
			});
			this.saveGridState(currentGrid);
		}
	}

	public generateLayout = (grid: IWatchBoxGridItem[]) => {
		const layouts = _.map(grid, item => ({
			h: item.h,
			i: item.key.toString(),
			static: false,
			w: item.width,
			x: item.x,
			y: item.y,
		}));
		this.setState({ layouts });
	}

	public saveGridState = (gridState: IWatchBoxGridItem[] = []) => {
		this.setState({ watchBoxGrid: gridState });
		const currentDate = (new Date()).toISOString();

		localStorage.setItem('watchbox_grid', JSON.stringify({ watchBoxGrid: gridState, timestamp: currentDate }));
		this.generateLayout(gridState);
	}

	public render() {
		const { watchBoxGrid, layouts, showModal } = this.state;
		return (
			<WatchesContainer className="w-100">
				<AddWatch
					showModal={showModal}
					closeHandler={this.handleCloseModal}
					addHandler={this.handleAddWidget}
				/>
				<nav className="navbar navbar pb-0">
					<div className="container-fluid">
						<div className="d-flex justify-center-content align-items-center">
							<button className="addBtn" style={{ height: '35px', width: '35px' }} onClick={this.handleOpenModal} >
								<FontAwesomeIcon size="1x" icon={faPlus} title="Minimize" />
							</button>
						</div>
					</div>
				</nav>
				<div className="center-side pb-1 px-1" style={{ width: '4320px' }}>
					<div id="watch-react-grid-layout" className="content pb-2 px-2" style={{ height: 'auto', overflow: 'inherit' }}>
						<ReactGrid
							className="child-watchbox"
							isResizable={false}
							cols={800}
							rowHeight={5}
							// verticalCompact
							autoSize={true}
							draggableHandle=".watch-handle"
							layout={layouts}
							onLayoutChange={this.layoutChange}
							verticalCompact={false}
							preventCollision={true}>
							{ watchBoxGrid && this.getWidgets() }
						</ReactGrid>
					</div>
				</div>
			</WatchesContainer>
		);
	}
}

const WatchesContainer = styled.div`
	background-color: ${({ theme }) => theme.core.darkBackground};
`;


const PanelWrapper = styled.div`
	background-color: ${({ theme }) => theme.core.lightBackground};
`;
